import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  Sparkles,
  MapPin,
  Calendar,
  Users,
  Clock,
  ImageOff,
  Share2,
  Link as LinkIcon,
  Check,
  Eye,
  MessageCircle,
  Mail,
  ArrowRight,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fileUrlPublic(path) {
  // Endpoint /files/* libera fotos de jobs finalizados sem auth.
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE}/api/files/${path}`;
}

function PhotoTile({ src, kind }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div className="aspect-square rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
        <ImageOff size={28} />
      </div>
    );
  }
  return (
    <motion.a
      whileHover={{ scale: 1.02 }}
      href={src}
      target="_blank"
      rel="noreferrer"
      className="block aspect-square rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 relative group"
    >
      <img
        src={src}
        alt={kind}
        loading="lazy"
        onError={() => setErrored(true)}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div
        className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white ${
          kind === "antes" ? "bg-amber-500/95" : "bg-emerald-500/95"
        }`}
      >
        {kind}
      </div>
    </motion.a>
  );
}

export default function PublicGallery() {
  const { jobId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/public/job/${jobId}/gallery`)
      .then(async (r) => {
        if (!r.ok) {
          const t = await r.text();
          throw new Error(t || "Galeria indisponível");
        }
        return r.json();
      })
      .then((d) => setData(d))
      .catch((e) => setErr(e.message || "Erro ao carregar galeria"))
      .finally(() => setLoading(false));
    // Increment view counter (fire & forget) — only once per session per job.
    try {
      const key = `cm:viewed:${jobId}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        fetch(`${API_BASE}/api/public/job/${jobId}/view`, { method: "POST" })
          .then((r) => (r.ok ? r.json() : null))
          .then((res) => {
            if (res?.views != null) {
              setData((prev) => (prev ? { ...prev, views: res.views } : prev));
            }
          })
          .catch(() => {});
      }
    } catch {
      // sessionStorage may be unavailable — silently ignore
    }
  }, [jobId]);

  const pageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  function shareMessage() {
    const clientName = data?.client_name || "este serviço";
    return `Olha o resultado do nosso serviço para ${clientName}! ✨🧽\n${pageUrl}`;
  }

  function openWhatsApp() {
    const txt = encodeURIComponent(shareMessage());
    window.open(`https://wa.me/?text=${txt}`, "_blank", "noopener");
  }

  async function nativeShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Antes e depois · ${data?.client_name || "Clean Manager USA"}`,
          text: shareMessage(),
          url: pageUrl,
        });
      } catch {
        // user cancelled — silent
      }
    } else {
      copyLink();
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  if (loading) {
    return (
      <div
        data-testid="public-gallery-loading"
        className="min-h-screen flex items-center justify-center bg-[#F8FAFC] text-slate-500"
      >
        <Loader2 className="animate-spin mr-2" size={18} /> Carregando galeria…
      </div>
    );
  }

  if (err || !data) {
    return (
      <div
        data-testid="public-gallery-error"
        className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] text-center px-6"
      >
        <ImageOff size={42} className="text-slate-400 mb-3" />
        <h1 className="text-xl font-semibold text-slate-900">
          Galeria indisponível
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-sm">
          O link pode estar expirado ou o serviço ainda não foi finalizado.
        </p>
      </div>
    );
  }

  const before = data.before_photos || [];
  const after = data.after_photos || [];
  const total = before.length + after.length;
  const maxRows = Math.max(before.length, after.length);

  return (
    <div
      data-testid="public-gallery-page"
      className="min-h-screen bg-[#F8FAFC] font-[Manrope] pb-16"
    >
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#001340] via-[#0a2256] to-[#1e40af] text-white px-5 sm:px-10 py-10 sm:py-14">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-blue-200 font-semibold bg-white/10 border border-white/15 px-2.5 py-1 rounded-full backdrop-blur-sm">
              <Sparkles size={12} /> Antes e depois
            </div>
            {data.views > 0 && (
              <div
                data-testid="gallery-views-badge"
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.04em] text-blue-100 bg-white/8 border border-white/12 px-2.5 py-1 rounded-full backdrop-blur-sm"
              >
                <Eye size={12} />
                {data.views} {data.views === 1 ? "pessoa viu" : "pessoas viram"} este serviço
              </div>
            )}
          </div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-4">
            {data.client_name}
          </h1>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-200/90">
            {data.address && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={14} /> {data.address}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={14} /> {fmtDate(data.scheduled_at)}
            </span>
            {data.team_name && (
              <span className="inline-flex items-center gap-1.5">
                <Users size={14} /> {data.team_name}
              </span>
            )}
            {data.duration_minutes && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} /> {data.duration_minutes} min
              </span>
            )}
          </div>

          {/* Share buttons */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="share-whatsapp-btn"
              onClick={openWhatsApp}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1ebe57] transition-colors shadow-[0_8px_24px_-12px_rgba(37,211,102,0.6)]"
            >
              <svg
                viewBox="0 0 32 32"
                width="16"
                height="16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035 1.06 2.722 1.06.302 0 .53-.072.717-.143.732-.273 1.62-.86 1.847-1.605.143-.487.058-.875-.057-1.045-.16-.245-.487-.388-.83-.516z M16.045 28.45c-2.21 0-4.422-.625-6.32-1.79l-.83-.487-4.4 1.122 1.18-4.244-.518-.83a11.948 11.948 0 0 1-1.835-6.367c.015-6.604 5.39-11.977 12.038-11.977 3.207 0 6.224 1.262 8.494 3.534 2.27 2.27 3.535 5.29 3.52 8.488-.014 6.604-5.39 11.978-12.038 11.978zm10.21-22.21A14.27 14.27 0 0 0 16.045 2c-7.927 0-14.39 6.45-14.404 14.39 0 2.534.66 5.01 1.925 7.198L1.5 31l7.595-1.992a14.453 14.453 0 0 0 6.937 1.762h.014c7.927 0 14.39-6.45 14.404-14.39 0-3.84-1.49-7.453-4.205-10.156z" />
              </svg>
              Compartilhar no WhatsApp
            </button>
            <button
              type="button"
              data-testid="share-native-btn"
              onClick={nativeShare}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-sm font-semibold hover:bg-white/25 transition-colors"
            >
              <Share2 size={15} /> Compartilhar
            </button>
            <button
              type="button"
              data-testid="copy-link-btn"
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-sm font-semibold hover:bg-white/25 transition-colors"
            >
              {copied ? <Check size={15} /> : <LinkIcon size={15} />}
              {copied ? "Link copiado!" : "Copiar link"}
            </button>
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="max-w-4xl mx-auto px-5 sm:px-10 mt-8">
        {total === 0 ? (
          <div
            data-testid="public-gallery-empty"
            className="text-center text-slate-500 py-16"
          >
            <ImageOff size={36} className="mx-auto text-slate-300 mb-2" />
            Nenhuma foto disponível para este serviço.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-1.5 text-amber-700 text-sm font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                ANTES ({before.length})
              </div>
              <div className="space-y-4">
                {Array.from({ length: Math.max(maxRows, 1) }).map((_, i) => (
                  <PhotoTile
                    key={`b-${i}`}
                    kind="antes"
                    src={fileUrlPublic(before[i])}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                DEPOIS ({after.length})
              </div>
              <div className="space-y-4">
                {Array.from({ length: Math.max(maxRows, 1) }).map((_, i) => (
                  <PhotoTile
                    key={`a-${i}`}
                    kind="depois"
                    src={fileUrlPublic(after[i])}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA: orçamento */}
      <div className="max-w-4xl mx-auto px-5 sm:px-10 mt-12">
        <div className="rounded-3xl bg-gradient-to-br from-[#001340] via-[#0a2256] to-[#1e40af] text-white p-7 sm:p-10 relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-blue-400/15 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-blue-200 font-semibold bg-white/10 border border-white/15 px-2.5 py-1 rounded-full backdrop-blur-sm">
                <Sparkles size={12} /> Gostou do resultado?
              </div>
              <h2 className="font-[Outfit] text-2xl sm:text-3xl font-semibold tracking-tight mt-3">
                Quer um orçamento para sua casa?
              </h2>
              <p className="text-sm sm:text-base text-slate-200/85 mt-2 max-w-md leading-relaxed">
                Fale com a gente em segundos pelo WhatsApp ou e-mail e receba um
                orçamento sem compromisso.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:min-w-[220px]">
              {data.company?.whatsapp && (
                <a
                  data-testid="cta-whatsapp-btn"
                  href={`https://wa.me/${String(data.company.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(`Olá! Vi a galeria de antes/depois (${pageUrl}) e gostaria de um orçamento para minha casa.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-between gap-2 px-5 py-3 rounded-2xl bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold text-sm transition-all hover:scale-[1.02] shadow-[0_8px_24px_-12px_rgba(37,211,102,0.7)]"
                >
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle size={16} /> Quer um orçamento?
                  </span>
                  <ArrowRight size={16} />
                </a>
              )}
              {data.company?.email && (
                <a
                  data-testid="cta-email-btn"
                  href={`mailto:${data.company.email}?subject=${encodeURIComponent("Solicitação de orçamento")}&body=${encodeURIComponent(`Olá! Vi a galeria de antes/depois (${pageUrl}) e gostaria de um orçamento para minha casa.`)}`}
                  className="inline-flex items-center justify-between gap-2 px-5 py-3 rounded-2xl bg-white/12 hover:bg-white/20 border border-white/20 text-white font-medium text-sm transition-colors backdrop-blur-md"
                >
                  <span className="inline-flex items-center gap-2">
                    <Mail size={16} /> Pelo e-mail
                  </span>
                  <ArrowRight size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>


      <footer className="text-center text-xs text-slate-400 mt-12">
        Compartilhado por <span className="font-semibold">{data.company?.name || "Clean Manager USA"}</span>
        {data.company?.website && (
          <>
            {" · "}
            <a
              href={data.company.website}
              target="_blank"
              rel="noreferrer"
              className="hover:text-slate-600 transition-colors"
            >
              {String(data.company.website).replace(/^https?:\/\//, "")}
            </a>
          </>
        )}
      </footer>
    </div>
  );
}
